import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ModuleShareRead, ModuleSharesPayload, ModuleV1Read, ShareUserRole, UserDetail, UserInfo } from '@ca/core/models';
import { ModuleSharingService, UserNotificationService, UserService } from '@ca/core/services';
import { NgbActiveModal, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import clone from 'clone-deep';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { FullNameWithEmailPipe } from '../../pipes/full-name-with-email/full-name-with-email.pipe';
import { FullNamePipe } from '../../pipes/full-name/full-name.pipe';

@Component({
    selector: 'ca-module-invite',
    templateUrl: './module-invite.component.html',
    styleUrls: ['./module-invite.component.scss'],
})
export class ModuleInviteComponent implements OnInit {
    @Input() module: ModuleV1Read;
    @ViewChild('searchUsersInput') searchUsersInput: ElementRef;

    moduleShares: ModuleShareRead[];
    userToAdd?: UserDetail;
    moduleSharesHaveChanged: boolean = false;
    shareUserRole = ShareUserRole;

    get userInfo(): UserInfo | undefined {
        return this.userService.myInfo;
    }

    constructor(
        private activeModal: NgbActiveModal,
        private fullNamePipe: FullNamePipe,
        private fullNameWithEmailPipe: FullNameWithEmailPipe,
        private moduleSharingService: ModuleSharingService,
        private userNotificationService: UserNotificationService,
        private userService: UserService,
    ) {
        this.searchUsers$ = this.searchUsers$.bind(this);
    }

    ngOnInit(): void {
        // We don't want to have the reference to the module.shares of the parent component
        this.moduleShares = clone(this.module.shares);
    }

    searchUsers$(text$: Observable<string>): Observable<UserDetail[]> {
        return text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            switchMap(textSearch => (textSearch.length < 2 ? [] : this.userService.searchUsersInfo$(textSearch))),
            map(x => x.slice(0, 20)),
        );
    }

    formatUser = (userDetail: UserDetail): string => this.fullNamePipe.transform(userDetail);

    formatUserWithEmail = (userDetail: UserDetail): string => this.fullNameWithEmailPipe.transform(userDetail);

    selectUser(event: NgbTypeaheadSelectItemEvent): void {
        const userToAdd: UserDetail = event.item;
        if (this.module.producerId === userToAdd.id) {
            this.userNotificationService.showWarning('MODULES.DETAIL.SHARING.NO_SELF_ADD_POSSIBLE');

            return;
        }
        if (userToAdd != null && this.moduleShares.findIndex((x: ModuleShareRead) => x.userId === userToAdd!.id) === -1) {
            this.moduleShares.push(this.mapUserInfoToSharedUser(userToAdd));
            this.userToAdd = undefined;
            setTimeout(() => {
                // unfortunately the only way to remove the content of the typeahead
                this.searchUsersInput.nativeElement.value = '';
            });
            this.moduleSharesHaveChanged = true;
        }
    }

    removePerson(userDetail: ModuleShareRead): void {
        const index: number = this.moduleShares.findIndex((u: ModuleShareRead) => u.userId === userDetail.userId);

        if (index > -1) {
            this.moduleShares.splice(index, 1);
            this.moduleSharesHaveChanged = true;
        }
    }

    save(): void {
        this.userNotificationService.showSpinner();

        this.moduleSharingService.updateModuleShares(this.moduleShares, this.module.id).subscribe(
            (sharesResponse: ModuleSharesPayload) => {
                this.userNotificationService.hideSpinner();
                this.userNotificationService.showSuccess('MODULES.INVITE.UPDATE_SHARES.SUCCESS');

                this.activeModal.close(sharesResponse.moduleShares);
            },
            () => {
                this.userNotificationService.hideSpinner();
                this.userNotificationService.showError('MODULES.INVITE.UPDATE_SHARES.ERROR');
            },
        );
    }

    changeAccessType(person: ModuleShareRead): void {
        if (person.role === ShareUserRole.Reader) {
            person.role = ShareUserRole.Contributor;
        } else if (person.role === ShareUserRole.Contributor) {
            person.role = ShareUserRole.Reader;
        }
        this.moduleSharesHaveChanged = true;
    }

    dismiss(reason?: string): void {
        this.activeModal.dismiss(reason);
    }

    private mapUserInfoToSharedUser(userInfo: UserDetail): ModuleShareRead {
        const { id, firstName, lastName, picture } = userInfo;

        return new ModuleShareRead(id, firstName, lastName, picture);
    }
}
